import React, { useRef, useState } from 'react';
import axios from 'axios';
import { API } from '../config';

const STATUS_COLORS = {
  queued:     'text-slate-400',
  processing: 'text-yellow-400',
  complete:   'text-green-400',
  error:      'text-red-400',
};
const STATUS_ICONS = { queued: '⬜', processing: '⏳', complete: '✅', error: '❌' };

export default function VideoUploader({ videos, onRefresh, onProcess, onSelect, selectedId, progress, processing }) {
  const inputRef    = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    await axios.post(`${API}/videos/upload`, fd);
    setUploading(false);
    onRefresh();
  };

  const handleDelete = async (id, ev) => {
    ev.stopPropagation();
    await axios.delete(`${API}/videos/${id}`);
    onRefresh();
  };

  return (
    <div className="flex flex-col gap-3">
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="w-full py-2 px-3 bg-brand hover:bg-brand-hover disabled:opacity-50
                   text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2">
        {uploading ? '⏫ Uploading…' : '📁 Upload Video'}
      </button>
      <input ref={inputRef} type="file" accept="video/*" className="hidden" onChange={handleUpload} />

      {progress && (
        <div className="bg-surface rounded-lg p-2">
          <div className="flex justify-between text-xs text-slate-400 mb-1">
            <span>Processing…</span>
            <span>{progress.percent?.toFixed(1)}%</span>
          </div>
          <div className="h-1.5 bg-surface-border rounded-full overflow-hidden">
            <div className="h-full bg-brand rounded-full transition-all"
                 style={{ width: `${progress.percent || 0}%` }} />
          </div>
        </div>
      )}

      <div className="text-xs text-slate-500 uppercase tracking-wide font-semibold mt-1">
        Video Queue ({videos.length})
      </div>

      <div className="flex flex-col gap-1 max-h-48 overflow-y-auto thin-scroll">
        {videos.map(v => (
          <div key={v.id}
            onClick={() => {
              if (v.status === 'queued') onProcess(v.id);
              else if (v.status === 'complete') onSelect?.(v.id);
            }}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs cursor-pointer
              transition-colors border
              ${selectedId === v.id
                ? 'bg-brand/20 border-brand/50'
                : 'bg-surface hover:bg-surface-border/30 border-transparent'}`}>
            <span>{STATUS_ICONS[v.status]}</span>
            <div className="flex-1 min-w-0">
              <div className="truncate text-slate-200">{v.filename}</div>
              <div className="text-slate-500">
                {v.duration_seconds ? `${v.duration_seconds.toFixed(1)}s` : ''}
                {v.status === 'queued' && <span className="ml-1 text-brand">(click to process)</span>}
                {v.status === 'complete' && <span className="ml-1 text-green-500">(click to watch)</span>}
              </div>
            </div>
            <span className={`${STATUS_COLORS[v.status]} shrink-0`}>{v.status}</span>
            <button onClick={(e) => handleDelete(v.id, e)}
              className="text-slate-600 hover:text-red-400 transition-colors shrink-0">✕</button>
          </div>
        ))}
        {videos.length === 0 && (
          <div className="text-slate-600 text-xs text-center py-3">No videos yet</div>
        )}
      </div>
    </div>
  );
}
